// EventsManagement: Admin CRUD for events, assign staff
import React, { useEffect, useState } from 'react';
import SuperAdminLayout from '../../layouts/SuperAdminLayout';
import { Box, Typography, Button, Table, TableHead, TableRow, TableCell, TableBody, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, CircularProgress } from '@mui/material';
import api from '../../services/api';
import NotificationSnackbar from '../../components/NotificationSnackbar';

function EventsManagement() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [modalOpen, setModalOpen] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [eventForm, setEventForm] = useState({ title: '', description: '', theme: '', startDate: '', endDate: '', status: 'active', venue: '' });
  const [modalLoading, setModalLoading] = useState(false);

  const fetchEvents = () => {
    setLoading(true);
    api.get('/api/events')
      .then(res => setEvents(res.data))
      .catch(err => setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to fetch events', severity: 'error' }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchEvents(); }, []);

  const handleDelete = async (id) => {
    setLoading(true);
    setSnackbar({ open: false, message: '', severity: 'info' });
    try {
      await api.delete(`/api/events/${id}`);
      fetchEvents();
      setSnackbar({ open: true, message: 'Event deleted successfully', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Delete failed', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => setSnackbar(s => ({ ...s, open: false }));

  const openModal = (event = null) => {
    setEditEvent(event);
    setEventForm(event ? {
      title: event.title || '',
      description: event.description || '',
      theme: event.theme || '',
      startDate: event.startDate ? event.startDate.substring(0, 10) : '',
      endDate: event.endDate ? event.endDate.substring(0, 10) : '',
      status: event.status || 'active',
      venue: event.venue || ''
    } : { title: '', description: '', theme: '', startDate: '', endDate: '', status: 'active', venue: '' });
    setModalOpen(true);
  };

  const handleFormChange = e => {
    const { name, value } = e.target;
    setEventForm(f => ({ ...f, [name]: value }));
  };

  const handleModalSave = async () => {
    setModalLoading(true);
    try {
      if (editEvent) {
        await api.put(`/api/events/${editEvent._id}`, eventForm);
        setSnackbar({ open: true, message: 'Event updated', severity: 'success' });
      } else {
        await api.post('/api/events', eventForm);
        setSnackbar({ open: true, message: 'Event created', severity: 'success' });
      }
      setModalOpen(false);
      fetchEvents();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Save failed', severity: 'error' });
    } finally {
      setModalLoading(false);
    }
  };

  return (
    <SuperAdminLayout>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Events Management
        </Typography>
        <Button variant="contained" color="primary" sx={{ mb: 2 }} onClick={() => openModal()}>Create Event</Button>
        <NotificationSnackbar
          open={snackbar.open}
          onClose={handleSnackbarClose}
          message={snackbar.message}
          severity={snackbar.severity}
        />
        {loading ? <Typography>Loading...</Typography> : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Theme</TableCell>
                <TableCell>Venue</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((event) => (
                <TableRow key={event._id}>
                  <TableCell>{event.title}</TableCell>
                  <TableCell>{event.theme}</TableCell>
                  <TableCell>{event.venue}</TableCell>
                  <TableCell>{event.startDate ? event.startDate.substring(0, 10) : ''}</TableCell>
                  <TableCell>{event.endDate ? event.endDate.substring(0, 10) : ''}</TableCell>
                  <TableCell>{event.status}</TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => openModal(event)}>Edit</Button>
                    <Button size="small" color="error" onClick={() => handleDelete(event._id)}>Delete</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {/* Event Create/Edit Modal */}
        <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{editEvent ? 'Edit Event' : 'Create Event'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField label="Title" name="title" value={eventForm.title} onChange={handleFormChange} fullWidth required />
              <TextField label="Theme" name="theme" value={eventForm.theme} onChange={handleFormChange} fullWidth />
              <TextField label="Venue" name="venue" value={eventForm.venue} onChange={handleFormChange} fullWidth required />
              <TextField label="Start Date" name="startDate" type="date" value={eventForm.startDate} onChange={handleFormChange} fullWidth required InputLabelProps={{ shrink: true }} />
              <TextField label="End Date" name="endDate" type="date" value={eventForm.endDate} onChange={handleFormChange} fullWidth required InputLabelProps={{ shrink: true }} />
              <TextField label="Description" name="description" value={eventForm.description} onChange={handleFormChange} fullWidth multiline rows={2} />
              <TextField select label="Status" name="status" value={eventForm.status} onChange={handleFormChange} fullWidth>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </TextField>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={handleModalSave} variant="contained" disabled={modalLoading}>{modalLoading ? <CircularProgress size={22} /> : 'Save'}</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </SuperAdminLayout>
  );
}

export default EventsManagement;
